import React, { createContext, useContext, useMemo, useState } from "react";
import { Icon } from "./icons";

export const navigation = [
  { group: "Executivo", items: [{ label: "Visão executiva", path: "/dashboard", icon: "overview" }] },
  { group: "Análise", items: [
    { label: "Performance", path: "/performance", icon: "trend" },
    { label: "Balanço", path: "/balanco", icon: "balance" },
    { label: "Indicadores", path: "/indicadores", icon: "chart" },
  ] },
  { group: "Gestão", items: [
    { label: "Orçamento", path: "/planejamento/orcamento", icon: "budget" },
    { label: "Relatórios", path: "/relatorios", icon: "report" },
    { label: "Apresentações", path: "/apresentacoes", icon: "presentation" },
    { label: "Mapas estratégicos", path: "/mapas-estrategicos", icon: "map" },
  ] },
  { group: "Administração", items: [
    { label: "Configurações", path: "/parametros/geral", icon: "settings" },
    { label: "Dados realizados", path: "/parametros/realizados", icon: "database" },
    { label: "Minhas preferências", path: "/parametros/minhas-preferencias", icon: "user" },
  ] },
];

export function findNavigationItem(pathname) {
  const exact = navigation.flatMap((group) => group.items.map((item) => ({ ...item, group: group.group }))).find((item) => item.path === pathname);
  if (exact) return exact;
  return navigation[0].items[0];
}

export function isActivePath(currentPath, itemPath) {
  return currentPath === itemPath || (itemPath !== "/dashboard" && currentPath.startsWith(`${itemPath}/`));
}

const AppNavigationContext = createContext(null);
const PageHeaderContext = createContext(null);

/** Use in page modules to make in-app navigation without react-router. */
export function useAppNavigation() {
  const value = useContext(AppNavigationContext);
  if (!value) throw new Error("useAppNavigation must be used inside AppShell.");
  return value;
}

/** Lets each screen place its title, explanation and relevant filters in the shared top header. */
export function usePageHeader() {
  return useContext(PageHeaderContext);
}

function initials(name) {
  return String(name || "CFO").split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

export function AppShell({ pathname, navigate, user, onLogout, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pageHeader, setPageHeader] = useState(null);
  const current = findNavigationItem(pathname);
  const context = useMemo(() => ({ pathname, navigate, user }), [pathname, navigate, user]);
  const pageHeaderContext = useMemo(() => ({
    setPageHeader: (content) => setPageHeader(content ? { pathname, content } : null),
  }), [pathname]);
  const headerContent = pageHeader?.pathname === pathname ? pageHeader.content : {
    eyebrow: current.group,
    title: current.label,
    description: null,
    actions: null,
  };

  function go(path) {
    navigate(path);
    setSidebarOpen(false);
  }

  return (
    <AppNavigationContext.Provider value={context}>
      <PageHeaderContext.Provider value={pageHeaderContext}>
      <div className={`app-shell ${sidebarOpen ? "sidebar-is-open" : ""}`}>
        <button className="sidebar-backdrop" type="button" aria-label="Fechar menu" onClick={() => setSidebarOpen(false)} />
        <aside className="app-sidebar" aria-label="Navegação principal">
          <div className="sidebar-top">
            <button className="brand-lockup" type="button" onClick={() => go("/dashboard")} aria-label="Ir para visão executiva">
              <span className="brand-cfo">CFO</span><span className="brand-autus">Autus</span>
            </button>
            <span className="environment-chip">FINANCE INTELLIGENCE</span>
          </div>

          <nav className="sidebar-nav">
            {navigation.map((section) => (
              <section className="nav-group" key={section.group} aria-label={section.group}>
                <p className="nav-group-label">{section.group}</p>
                <div className="nav-group-links">
                  {section.items.map((item) => {
                    const active = isActivePath(pathname, item.path);
                    return <button type="button" className={`sidebar-link ${active ? "is-active" : ""}`} onClick={() => go(item.path)} key={item.path} aria-current={active ? "page" : undefined}>
                      <Icon name={item.icon} size={18} /><span>{item.label}</span>
                    </button>;
                  })}
                </div>
              </section>
            ))}
          </nav>

          <div className="sidebar-bottom">
            <div className="sidebar-profile">
              <span className="avatar avatar-small">{initials(user?.nome || user?.name)}</span>
              <span className="profile-copy"><strong>{user?.nome || user?.name || "Equipe Autus"}</strong><small>Ambiente corporativo</small></span>
            </div>
            <button type="button" className="logout-button" onClick={onLogout}><Icon name="logout" size={18} /> Sair</button>
          </div>
        </aside>

        <div className="app-stage">
          <header className="app-topbar">
            <button className="mobile-menu-button" type="button" onClick={() => setSidebarOpen(true)} aria-label="Abrir menu"><Icon name="menu" /></button>
            <div className="topbar-page-content">
              <div className="topbar-context"><span>{headerContent.eyebrow}</span><strong>{headerContent.title}</strong></div>
              {headerContent.actions && <div className="topbar-page-actions">{headerContent.actions}</div>}
            </div>
            <div className="topbar-actions">
              <button className="icon-button notification-button" type="button" aria-label="Notificações"><Icon name="bell" size={19} /><i /></button>
              <button className="topbar-user" type="button" onClick={() => go("/parametros/minhas-preferencias")} aria-label="Abrir minhas preferências"><span className="avatar">{initials(user?.nome || user?.name)}</span><Icon name="chevron" size={16} /></button>
            </div>
          </header>
          <main className="app-main">{children}</main>
        </div>
      </div>
      </PageHeaderContext.Provider>
    </AppNavigationContext.Provider>
  );
}

export function PageFrame({ eyebrow, title, description, actions, children, className = "" }) {
  return <div className={`page-frame ${className}`.trim()}>
    {(eyebrow || title || description || actions) && <header className="page-header">
      <div className="page-heading">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        {title && <h1>{title}</h1>}
        {description && <p className="page-description">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>}
    {children}
  </div>;
}

export function SectionCard({ title, subtitle, action, children, className = "" }) {
  return <section className={`section-card ${className}`.trim()}>
    {(title || subtitle || action) && <header className="section-card-header"><div>{title && <h2>{title}</h2>}{subtitle && <p>{subtitle}</p>}</div>{action}</header>}
    {children}
  </section>;
}

export function MetricCard({ label, value, variation, tone = "neutral", detail, icon }) {
  const variationText = variation === undefined || variation === null ? null : `${Number(variation) >= 0 ? "+" : ""}${variation}%`;
  return <article className={`metric-card metric-${tone}`}>
    <div className="metric-card-top"><span>{label}</span>{icon && <span className="metric-icon"><Icon name={icon} size={18} /></span>}</div>
    <strong>{value}</strong>
    <div className="metric-foot">{variationText && <span className={Number(variation) >= 0 ? "positive" : "negative"}>{variationText}</span>}{detail && <small>{detail}</small>}</div>
  </article>;
}

export function DataTable({ columns, rows, emptyMessage = "Nenhum dado para este recorte." }) {
  if (!rows?.length) return <EmptyState title={emptyMessage} compact />;
  return <div className="table-wrap"><table className="data-table"><thead><tr>{columns.map((column) => <th key={column.key} className={column.align ? `align-${column.align}` : ""}>{column.label}</th>)}</tr></thead><tbody>{rows.map((row, rowIndex) => <tr key={row.id || row.key || rowIndex}>{columns.map((column) => <td key={column.key} className={column.align ? `align-${column.align}` : ""}>{column.render ? column.render(row, rowIndex) : row[column.key]}</td>)}</tr>)}</tbody></table></div>;
}

export function EmptyState({ title = "Nada por aqui ainda", description, action, compact = false }) {
  return <div className={`empty-state ${compact ? "is-compact" : ""}`}><span className="empty-state-icon"><Icon name="chart" size={22} /></span><div><strong>{title}</strong>{description && <p>{description}</p>}{action}</div></div>;
}

export function PrimaryButton({ children, className = "", icon, ...props }) {
  return <button className={`button button-primary ${className}`.trim()} {...props}>{icon && <Icon name={icon} size={17} />}{children}</button>;
}

export function SecondaryButton({ children, className = "", icon, ...props }) {
  return <button className={`button button-secondary ${className}`.trim()} {...props}>{icon && <Icon name={icon} size={17} />}{children}</button>;
}
